import qs from 'qs';
import { get } from "lodash";
import { API_URL, NUMBER_OF_VACANCIES_PER_PAGE } from '../configs'
import createAxiosInstance from "./protected-axios-instance";
import createAxiosSentryInstance from "./sentry-axios-instance";

//Use this protected instance when it's important not to call a request with expired token
const axiosProtectedInstance = createAxiosInstance();
//Use this instance when request should not be protected (doesn't require a token) and error should be logged to Sentry
const axiosSentryInstance = createAxiosSentryInstance();

export const AdminUserAgent = {
  signUp: async (config) => axiosSentryInstance.post(`${API_URL}/signup`, {...config}),

  signUpDirect: async (config) => axiosSentryInstance.post(`${API_URL}/signup_direct`, {...config}),

  signIn: async (config, userAuthInformation) => axiosSentryInstance.post(`${API_URL}/login`, qs.stringify({ ...config }), {
    headers: {
      'X-Device-Info': get(userAuthInformation, "device"),
      'X-Browser-Info': get(userAuthInformation, "browser"),
      'X-OS-Info': get(userAuthInformation, "os"),
    },
   }),

  getCurrentUser: (token) => axiosProtectedInstance.get('/users/current'),

  sendEmailToSupport: (token, question) => axiosProtectedInstance.post('/support/email', { question }),

  getVacanciesByStatus: (token, statuses, offset, limit = NUMBER_OF_VACANCIES_PER_PAGE, search = "") => {
    const params = new URLSearchParams();
    if (limit) params.set('limit', limit);
    if (offset) params.set('offset', offset);
    if (search) params.set('search', search);
    const query = params.toString();
    return axiosProtectedInstance.post(`/vacancies/status${query ? `?${query}` : ""}`, statuses, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  },

  resetPassword: (config) => axiosSentryInstance.post(`${API_URL}/forget_password`, { ...config }, {headers: { "Accept": "application/json" }}),

  newPassword: (config) => axiosSentryInstance.post(`${API_URL}/reset_password`, {
    new_password: config.new_password,
    confirm_password: config.confirm_password,
    access_token: config.access_token
  }, {
    headers: { "Accept": "application/json" }
  }),

  setProfilePicture: (token, config) =>  axiosSentryInstance.post('/users/current/logo', config, { headers: { 'Content-Type': 'multipart/form-data', "Authorization": `Bearer ${token}` }}),

  getProfilePicture: (token) => axiosSentryInstance.get(`${API_URL}/users/current/logo`, { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` } }),

  updateCurrentUser: (token, config) => axiosProtectedInstance.put(`/users/current`, { ...config }),

  updatePasswordForUser: (token, config) => axiosProtectedInstance.post(`/change_password`, { ...config }),

  getVacancy: (token, vacancyId) => axiosProtectedInstance.get(`/vacancies/${vacancyId}`),
  getVacancyCandidates: (token, vacancyId) => axiosProtectedInstance.get(`/candidates/vacancy/${vacancyId}`),

  getWhatsappIntegration: (token) => axiosProtectedInstance.get(`/whatsapp_integrations/current`),

  getWhatsappIntegrationStatus: (token) => axiosProtectedInstance.get(`/whatsapp_integrations/status/current`),

  getWhatsappTemplates: (token) => axiosProtectedInstance.get(`/whatsapp_integrations/templates/current`),

  updateWhatsappTemplate: (token, slot, payload) => axiosProtectedInstance.put(`/whatsapp_integrations/templates/${slot}`, payload),

  syncWhatsappTemplates: (token) => axiosProtectedInstance.post(`/whatsapp_integrations/templates/sync`),

  updateWhatsappIntegrationSettings: (token, payload) => axiosProtectedInstance.put(`/whatsapp_integrations/current`, payload),

  completeWhatsappEmbeddedSignup: (token, payload) => axiosProtectedInstance.post(`/whatsapp_integrations/embedded_signup`, payload),

  getUnlinkedMetaAds: (token) => axiosProtectedInstance.get(`/whatsapp_ad_leads/unlinked/current`),

  getCandidateInterview: (token, candidateId) => axiosProtectedInstance.get(`/candidates/${candidateId}/interview`),
  getCandidateCv: (token, candidateId, signal) =>
    axiosProtectedInstance.get(`/candidates/${candidateId}/cv`, { responseType: "blob", signal }),
  getCandidateInterviewAudioHead: (token, candidateId) =>
    axiosProtectedInstance.get(`/candidates/${candidateId}/interview/audio-head`, { responseType: "arraybuffer" }),

  // ── Studio (real backend contract): vacancy create + interview builder + public apply ──
  createVacancy: (token, payload) => axiosProtectedInstance.post(`/vacancies`, payload),

  updateVacancy: (token, vacancyId, payload) => axiosProtectedInstance.put(`/vacancies/${vacancyId}`, payload),

  linkMetaAdToVacancy: (token, vacancyId, metaAdId) => axiosProtectedInstance.post(`/vacancies/${vacancyId}/meta_ads`, { meta_ad_id: metaAdId }),

  getInterviewScript: (token, vacancyId, language) =>
    axiosProtectedInstance.get(`/interview_scripts?vacancy_id=${vacancyId}&interview_type_name=screening&language=${language}`),

  createInterviewScript: (token, payload) => axiosProtectedInstance.post(`/interview_scripts`, payload),

  inferInterviewScript: (token, vacancyId, interviewTypeName, language, complexity = "short") =>
    axiosProtectedInstance.post(`/infer/interview_script/${vacancyId}?interview_type_name=${interviewTypeName}&language=${language}&complexity=${complexity}`),
  translateInterviewScript: (token, vacancyId, targetLanguage) =>
    axiosProtectedInstance.post(`/infer/interview_script/${vacancyId}/translate?interview_type_name=screening&target_language=${targetLanguage}`),

  inferDescriptionByUrl: (token, url) => axiosProtectedInstance.post(`/infer/description/by_url`, null, { params: { url } }),

  inferDescriptionByFile: (token, file) => {
    const form = new FormData();
    form.append("file", file);
    return axiosProtectedInstance.post(`/infer/description/by_file`, form, { headers: { "Content-Type": "multipart/form-data" } });
  },

  inferVacancyInput: (token, description) => axiosProtectedInstance.post(`/infer/vacancy_input`, { description }),

  createInterviewSection: (token, payload) => axiosProtectedInstance.post(`/interview_sections`, payload),

  updateInterviewSection: (token, sectionId, payload) => axiosProtectedInstance.put(`/interview_sections/${sectionId}`, payload),

  createInterviewQuestion: (token, payload) => axiosProtectedInstance.post(`/interview_questions`, payload),

  updateInterviewQuestion: (token, questionId, payload) => axiosProtectedInstance.put(`/interview_questions/${questionId}`, payload),

  // Public, unauthenticated candidate apply (no JWT) — returns interview_url to redirect to.
  applyForInterview: (payload) => axiosSentryInstance.postForm(`${API_URL}/signup/mvp/interview`, payload, { headers: { "Accept": "application/json" } }),

  logout: (token) => axiosProtectedInstance.post(`${API_URL}/logout`, {}, { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` } }),

  getSharedVacancies: (token, offset = 0) => axiosProtectedInstance.get(`/vacancies/shared?status=open&limit=${NUMBER_OF_VACANCIES_PER_PAGE}&offset=${offset}`),

  getAllUsersFromOrganization: (token) => axiosSentryInstance.get(`${API_URL}/users`, { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` } }),

  getAllNotifications: (token) => axiosProtectedInstance.get(`${API_URL}/notifications`, { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` } }),

  markNotificationAsRead: (token, notificationId, config) => axiosProtectedInstance.put(`${API_URL}/notifications/${notificationId}`, { ...config }, { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` } }),

  getCustomer: (token) => axiosSentryInstance.get(`${API_URL}/customers/current`, { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` } }),

  updateCurrentCustomer: (token, config) => axiosProtectedInstance.put(`/customers/current`, { ...config }),

  inviteNewUser: (token, config) => axiosProtectedInstance.post(`${API_URL}/invite_user`, { ...config }, { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` } }),

  inviteUsers: (token, file) => axiosProtectedInstance.post(`${API_URL}/invite_users`, file, { headers: { "Accept": "application/json", "Content-Type": "multipart/form-data", "Authorization": `Bearer ${token}` } }),

  createCompany: (token, configuration) => axiosProtectedInstance.post(`/companies`, {...configuration}, { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` }}),

  getCurrentCompany: (token) => axiosSentryInstance.get(`/companies/current`, { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` }}),

  updateCurrentCompany: (token, companyId, config) => axiosProtectedInstance.put(`/companies/current?company_id=${companyId}`, config),

  setCompanyLogo: (token, config) =>  axiosSentryInstance.post('/companies/current/logo', config, { headers: { 'Content-Type': 'multipart/form-data', "Authorization": `Bearer ${token}` }}),

  setCustomerAssets: (token, config) =>  axiosSentryInstance.post('/customers/assets/upload/current', config, { headers: { 'Content-Type': 'multipart/form-data', "Authorization": `Bearer ${token}` }}),

  getCustomerAssets: (token) => axiosSentryInstance.get('/customers/assets/current', { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` }}),

  removeCustomerBackground: (token) => axiosProtectedInstance.delete('/customers/assets/background/current'),

  loginWithGoogle: (token, userAuthInformation) => axiosSentryInstance.post(`/login/google?token=${token}`, null, {
    headers: {
      "Accept": "application/json",
      'X-Device-Info': get(userAuthInformation, "device"),
      'X-Browser-Info': get(userAuthInformation, "browser"),
      'X-OS-Info': get(userAuthInformation, "os"),
    }
  }),

  loginWithFacebook: (token, userAuthInformation) => axiosSentryInstance.post(`/login/facebook?token=${token}`, null, {
    headers: {
      "Accept": "application/json",
      'X-Device-Info': get(userAuthInformation, "device"),
      'X-Browser-Info': get(userAuthInformation, "browser"),
      'X-OS-Info': get(userAuthInformation, "os"),
    }
  }),

  signUpWithEmail: async ({ config }) => axiosSentryInstance.post(`${API_URL}/signup/email`, {...config}, { headers: { "Accept": "application/json" } }),

  signUpWithGoogle: async ({ token, user_type, locale, access_token }) => axiosSentryInstance.post(`${API_URL}/signup/google?token=${token}&user_type=${user_type}${locale ? `&locale=${locale}` : ''}${access_token ? `&access_token=${access_token}` : ''}`, null, { headers: { "Accept": "application/json" } }),

  signUpWithFacebook: async ({ token, user_type, locale }) => axiosSentryInstance.post(`${API_URL}/signup/facebook?token=${token}&user_type=${user_type}${locale ? `&locale=${locale}` : ''}`, null, { headers: { "Accept": "application/json" } }),

  sendVerifyEmailHi5: (token) => axiosProtectedInstance.post(`${API_URL}/verify/email/send`, null, { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` } }),

  checkVerifyEmail: (config) => axiosSentryInstance.post(`${API_URL}/verify/email/check`, {...config}, { headers: { "Accept": "application/json"} }),

  sendVerifyPhone: async (token, channel) => axiosProtectedInstance.post(`${API_URL}/verify/phone/send?channel=${channel}`, null, { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` }}),

  checkVerifyPhone: async (token, code) => axiosProtectedInstance.post(`${API_URL}/verify/phone/check?code=${code}`, null, { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` }}),

  checkTokenValidityResetPassword: (accessToken) => axiosSentryInstance.get(`${API_URL}/validate_email_token`, { params: { access_token: accessToken } }),

  fetchAccessTokenForInterview: async ({ token, userAuthInformation }) => axiosSentryInstance.post(`${API_URL}/login/interview_request?token=${token}`, null,
    {
      headers: {
        "Accept": "application/json",
        "X-Device-Info": get(userAuthInformation, "device"),
        "X-Browser-Info": get(userAuthInformation, "browser"),
        "X-OS-Info": get(userAuthInformation, "os"),
      },
    }
  ),

  updateInterviewWithFeedback: async ({token, interviewId, feedbackScore, feedbackText}) => axiosSentryInstance.put(`${API_URL}/interviews/${interviewId}`, { feedback_score: feedbackScore, feedback_text: feedbackText },  { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` }}),

  startAvatarInterview: (token, config) => axiosSentryInstance.post(`${API_URL}/interviews/start`, config, { headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }}),

  demoAvatarInterview: (config) => axiosSentryInstance.post(`${API_URL}/interviews/demo/start`, config, { headers: { "Content-Type": "application/json" }}),

  checkAmountOfActiveInterviews: () => axiosSentryInstance.post(`${API_URL}/interviews/sessions/active`),

  logoutInterview: (token) => axiosSentryInstance.post(`/logout`, {}, { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` } }),

  getInterviewUrlByEmail: async ({ email, jobId, language }) => axiosSentryInstance.post(`${API_URL}/interview_requests/url/email`, { email, job_id: jobId, language }, { headers: { "Accept": "application/json" }}),

  getCustomerAssetsQuickstart: async ({ customerDomain }) => axiosSentryInstance.get(`${API_URL}/customers/assets`, { params: { customer_domain: customerDomain } }),

  getCustomerAssetsForVacancy: async ({ vacancyUuid }) => axiosSentryInstance.get(`${API_URL}/customers/assets/vacancy/${vacancyUuid}`),

  getVacancyLanguages: async ({ jobId }) => axiosSentryInstance.get(`${API_URL}/vacancies/languages`, { params: { job_id: jobId } }),

  inferLocationInput: (token, sessionToken, text, isFullyRemote) => axiosSentryInstance.get(`${API_URL}/infer/location/autocomplete`, { params: { input_text: text, sessiontoken: sessionToken, is_remote: isFullyRemote }, headers: { "Accept": "application/json", "Authorization": `Bearer ${token}`} }),

  getCalendarRequestSchedule: async ({ token, interviewRequestId }) => axiosSentryInstance.get(`${API_URL}/calendar_events/available_slots`, { params: { interview_request_id: interviewRequestId }, headers: { "Accept": "application/json", "Authorization": `Bearer ${token}`}}),

  scheduleInterviewTimeSlot: async (token, config) => axiosSentryInstance.post(`${API_URL}/calendar_events/schedule`, config, { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` }})

}
